import { Editor } from '@tiptap/react';

import { v4 as uuid } from 'uuid';

import Axios from 'axios';

import Logger from 'utils/logger';

import API from 'utils/api';
import { getErrorMessage } from 'utils/api/errors';
import { fileTooLargeMessage, isOverUploadLimit } from 'utils/uploadLimits';

import { EntityModules } from 'core/constants';

export const uploadBase64ImageToServer = async (
  currentEditor: Editor,
  file: File,
  blobUrl: string,
  uploadId: string,
  entityId: string,
  enqueueErrorSnackbar: (message: string) => void
): Promise<void> => {
  try {
    const cancelTokenSource = Axios.CancelToken.source();
    const { data } = await API.entities.uploadEntityFile(
      EntityModules.Documents,
      entityId,
      file,
      () => {},
      cancelTokenSource
    )();

    URL.revokeObjectURL(blobUrl);
    const fileUrl: string = data.file || '';

    currentEditor.state.doc.descendants((node, pos) => {
      if (node.type.name === 'image' && node.attrs['data-upload-id'] === uploadId) {
        currentEditor
          .chain()
          .setNodeSelection(pos)
          .updateAttributes('image', { src: fileUrl, 'data-upload-id': null, 'data-uploading': false })
          .run();
        return false;
      }
      return true;
    });
  } catch (e) {
    URL.revokeObjectURL(blobUrl);
    Logger.error(`Failed to upload pasted image for entity ${entityId}`, e);
    const errorMessage = Axios.isAxiosError(e) ? getErrorMessage(e, 'Failed to upload image') : 'Failed to upload image';
    enqueueErrorSnackbar(errorMessage);

    currentEditor.state.doc.descendants((node, pos) => {
      if (node.type.name === 'image' && node.attrs['data-upload-id'] === uploadId) {
        currentEditor.chain().setNodeSelection(pos).deleteSelection().run();
        return false;
      }
      return true;
    });
  }
};

export const uploadAttachmentFunction = (
  currentEditor: Editor,
  files: File[],
  pos: number,
  entityId: string,
  enqueueErrorSnackbar: (message: string) => void
): void => {
  files.forEach(async (file) => {
    if (isOverUploadLimit(file)) {
      enqueueErrorSnackbar(fileTooLargeMessage(file));
      return;
    }

    const uploadId = uuid();

    // Optimistic insert: image placeholder if image/*, otherwise attachment chip
    if (file.type.startsWith('image/')) {
      const previewUrl = URL.createObjectURL(file); // cheaper than dataURL
      currentEditor
        .chain()
        .insertContentAt(pos, [
          {
            type: 'image',
            attrs: { src: previewUrl, 'data-upload-id': uploadId, 'data-uploading': true },
          },
          {
            type: 'paragraph',
          },
        ])
        .focus()
        .run();
    } else {
      currentEditor
        .chain()
        .insertContentAt(pos, [
          {
            type: 'attachment',
            attrs: {
              name: file.name,
              size: file.size,
              mime: file.type,
              createdAt: new Date().toISOString(),
              href: '', // final URL comes later
              uploading: true,
              uploadId,
              progress: 0,
            },
          },
          {
            type: 'paragraph',
          },
        ])
        .focus()
        .run();
    }

    try {
      const cancelTokenSource = Axios.CancelToken.source();
      const { data } = await API.entities.uploadEntityFile(
        EntityModules.Documents,
        entityId,
        file,
        (progressEvent) => {
          if (progressEvent.loaded && progressEvent.total) {
            const percentComplete = Math.round((progressEvent.loaded / progressEvent.total) * 100);

            // Update the attachment node with progress
            currentEditor.state.doc.descendants((node, posFound) => {
              if (node.type.name === 'attachment' && node.attrs.uploadId === uploadId) {
                currentEditor
                  .chain()
                  .setNodeSelection(posFound)
                  .updateAttributes('attachment', {
                    progress: percentComplete,
                  })
                  .run();
                return false;
              }
              return true;
            });
          }
        },
        cancelTokenSource
      )();

      const fileUrl: string = data.file || '';

      // Update the inserted node:
      if (file.type.startsWith('image/')) {
        // Store the proxy URL (files/{uuid}/download/) — never expires, always generates fresh presigned URL
        currentEditor.state.doc.descendants((node, posFound) => {
          if (node.type.name === 'image' && node.attrs['data-upload-id'] === uploadId) {
            currentEditor
              .chain()
              .setNodeSelection(posFound)
              .updateAttributes('image', { src: fileUrl, 'data-upload-id': null, 'data-uploading': false })
              .run();
            return false;
          }
          return true;
        });
      } else {
        currentEditor.state.doc.descendants((node, posFound) => {
          if (node.type.name === 'attachment' && node.attrs.uploadId === uploadId) {
            currentEditor
              .chain()
              .setNodeSelection(posFound)
              .updateAttributes('attachment', {
                href: fileUrl,
                uploading: false,
                creator: data.creator,
                created: data.created,
                file: data.file,
                filename: data.filename,
                thumbnail: data.thumbnail,
                size: data.size,
                name: data.filename,
              })
              .insertContentAt(posFound + 1, [
                {
                  type: 'paragraph',
                },
              ])
              .run();
            return false;
          }
          return true;
        });
      }
    } catch (e) {
      Logger.error(`Invalid add file API result: ${EntityModules.Documents} ID: ${entityId}.`, e);
      const errorMessage = Axios.isAxiosError(e)
        ? getErrorMessage(e, 'Failed to upload file')
        : 'Failed to upload file';
      enqueueErrorSnackbar(errorMessage);

      if (file.type.startsWith('image/')) {
        currentEditor.state.doc.descendants((node, posFound) => {
          if (node.type.name === 'image' && node.attrs['data-upload-id'] === uploadId) {
            currentEditor.chain().setNodeSelection(posFound).deleteSelection().run();
            return false;
          }
          return true;
        });
      } else {
        currentEditor.state.doc.descendants((node, posFound) => {
          if (node.type.name === 'attachment' && node.attrs.uploadId === uploadId) {
            currentEditor
              .chain()
              .setNodeSelection(posFound)
              .updateAttributes('attachment', { uploading: false, error: true, errorMessage })
              .run();
            return false;
          }
          return true;
        });
      }
    }
  });
};
